/**
 * The dashboard's "What Gyro could not do" card (owner, 2026-10-08): the service's anonymous counts (GET /v1/admin/dashboard
 * `unmet`, services/gyro-svc/src/unmet.ts) in plain words. Categories name what people asked for, kinds why Gyro could not
 * do it; the roadmap works from these counts (docs/STRATEGY-gyro-capabilities-2026-10-08.md).
 */
import type { UnmetSummary } from './api';

export const UNMET_CATEGORY_LABELS: Record<string, string> = {
  email: 'Email', calendar: 'Calendar and meetings', tasks: 'To Do and Planner', teams: 'Teams', files: 'Files (OneDrive, SharePoint)',
  it_request: 'IT requests', project: 'Project views', deltek: 'Deltek', paycom: 'Paycom', fireflies: 'Fireflies', bluebeam: 'Bluebeam',
  dropbox: 'Dropbox', procore: 'Procore', acc: 'Autodesk Construction Cloud', orbit: 'Orbit', asana: 'Asana', nebula: 'Nebula',
  desktop_control: 'Desktop control', knowledge: 'Company knowledge', other: 'Other',
};

export const UNMET_KIND_LABELS: Record<string, string> = {
  no_tool: 'no tool for it',
  not_connected: 'system not connected',
  no_permission: 'permission missing',
  refused: 'refused by policy',
  other: 'other',
};

export interface UnmetRow {
  category: string;
  kind: string;
  count: number;
  /** The share of everything counted, 0 to 100. */
  share: number;
}

/** The summary's rows with plain names and shares, highest first; empty when nothing was counted. */
export function unmetRows(summary: UnmetSummary | undefined): UnmetRow[] {
  if (!summary || summary.total <= 0) return [];
  return summary.rows.map((row) => ({
    category: UNMET_CATEGORY_LABELS[row.category] ?? row.category,
    kind: UNMET_KIND_LABELS[row.kind] ?? row.kind,
    count: row.count,
    share: Math.round((row.count / summary.total) * 100),
  }));
}

/** One line for the card's head. */
export function unmetHeadline(summary: UnmetSummary | undefined): string {
  if (!summary) return 'Not reported by this service version.';
  if (summary.total === 0) return `Nothing counted in the last ${summary.windowDays} days.`;
  const top = summary.rows[0];
  const topText = top ? ` Most often: ${UNMET_CATEGORY_LABELS[top.category] ?? top.category} (${UNMET_KIND_LABELS[top.kind] ?? top.kind}).` : '';
  return `${summary.total.toLocaleString('en-US')} request${summary.total === 1 ? '' : 's'} Gyro could not finish in the last ${summary.windowDays} days.${topText}`;
}
